import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { motion } from "framer-motion";

interface Props {
  presidenId: number;
  nama: string;
  jabatan: string;
}

type SubTab = "kementerian" | "ai";

const KinerjaSection: React.FC<Props> = ({ presidenId, nama, jabatan }) => {
  const [subTab, setSubTab] = useState<SubTab>("kementerian");

  const [loading, setLoading] = useState(false);
  const [dataKementerian, setDataKementerian] = useState<string | null>(null);
  const [dataAI, setDataAI] = useState<string | null>(null);

  // fetch data kementerian dari BE
  useEffect(() => {
    if (subTab !== "kementerian") return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = Cookies.get("token");
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/kinerja/${presidenId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) throw new Error("Belum ada update dari kementerian.");
        const result = await res.json();
        setDataKementerian(result.deskripsi || "Belum ada update dari kementerian.");
      } catch (err) {
        console.error(err);
        setDataKementerian("Belum ada update dari kementerian.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [subTab, presidenId]);

  // fetch data AI
  useEffect(() => {
    if (subTab !== "ai") return;

    const fetchAI = async () => {
      setLoading(true);
      try {
        const token = Cookies.get("token");
        const prompt = `Gunakan informasi terbaru hingga tahun 2025 dari sumber terpercaya (Wikipedia, portal berita resmi, atau situs pemerintah) untuk menjawab pertanyaan ini: apa yang baru dikerjakan oleh ${jabatan} ${nama}?`;

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/openai-generate`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ prompt }),
        });

        if (!res.ok) throw new Error("Gagal ambil data AI");

        const result = await res.json();
        setDataAI(result.choices?.[0]?.message?.content || "Tidak ada informasi AI.");
      } catch (err) {
        console.error(err);
        setDataAI("Terjadi kesalahan mengambil data AI.");
      } finally {
        setLoading(false);
      }
    };

    fetchAI();
  }, [subTab, jabatan, nama]);

  return (
    <div>
      {/* Sub Tabs */}
      <div className="flex gap-4  pb-2 justify-center flex-wrap">
        <button onClick={() => setSubTab("kementerian")} className={`pb-2 font-medium ${subTab === "kementerian" ? "border-2 rounded-lg border-yellow-600 text-yellow-600 px-4 py-2" : "text-gray-500 hover:text-yellow-600"}`}>
          Hasil Informasi Dari Kementrian Terkait
        </button>
        <button onClick={() => setSubTab("ai")} className={`pb-2 font-medium ${subTab === "ai" ? "border-2 rounded-lg border-yellow-600 text-yellow-600 px-4 py-2" : "text-gray-500 hover:text-yellow-600"}`}>
          Hasil Informasi Dari Sumber Terbuka
        </button>
      </div>

      {/* Konten */}
      <div className="mt-4">
        {loading ? (
          <p className="text-center text-gray-500">Mencari informasi...</p>
        ) : (
          <>
            {subTab === "kementerian" && (
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="whitespace-pre-line text-gray-800">
                <div className="text-gray-700 flex justify-center mb-4 text-xl font-semibold">Hasil Informasi</div>
                {dataKementerian}
              </motion.div>
            )}

            {subTab === "ai" && (
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="whitespace-pre-line text-gray-800">
                <div className="text-gray-700 flex justify-center mb-4 text-xl font-semibold">Hasil Informasi</div>
                {dataAI}
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default KinerjaSection;
